import Text from "common/Typography/Text";
import Button from "common/Buttons/Button";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  UPGRADE_SUBSCRIPTION_SETTINGS_PATH,
} from "common/constants";
import { useHistory } from "react-router-dom";
import Flex from "common/Boxes/Flex";
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import ExtraQuotaText from "features/settings/Billing/ExtraQuotaText";
import CornerStarBanner from "common/misc/CornerStarBanner";
import { NextPlanData } from "@shared/features/settings/types";
import styled, { useTheme } from "styled-components";
import darkModeBackgroundElement from "images/quote_modal_background_dark_mode.png";
import backgroundElement from "images/quote_modal_background.png";
import { useUser } from "@shared/utils/hooks/misc";

interface PassedQuotaContentProps {
  currentUsageObjectText: string;
  nextPlanData?: NextPlanData;
}

const PassedQuotaContent = ({
  currentUsageObjectText,
  nextPlanData,
}: PassedQuotaContentProps) => {
  const theme = useTheme();
  const history = useHistory();
  const { user } = useUser();
  const removeNextPlanData = !nextPlanData;

  const getSortedNextPlanUsages = () => {
    return nextPlanData?.limits.map((limitData, index) => {
      const isLast = index === nextPlanData.limits.length - 1;
      return (
        <Flex
          $justifyContent="flex-start"
          style={{ marginBottom: 6 }}
          key={index}
        >
          <CheckCircleFilled
            style={{
              color: theme.colors.primaryBlue,
              marginRight: 12,
              fontSize: 16,
            }}
          />
          <Flex
            $alignItems="flex-end"
            $justifyContent="space-between"
            style={{ flex: 1 }}
          >
            <Text style={{ fontWeight: 500 }}>
              {limitData.baseLimit} {limitData.objectName}
              <ExtraQuotaText
                extraQuota={limitData.extraLimit}
                textStyle={{ fontWeight: 500 }}
              />
            </Text>
            {isLast && <AndMoreText>+ And more!</AndMoreText>}
          </Flex>
        </Flex>
      );
    });
  };

  const getHighlightedText = () => {
    return (
      <Background>
        <CornerStarBanner />
        <Text style={{ fontWeight: "700", marginBottom: 12 }}>
          Get More with the {nextPlanData?.name} plan:
        </Text>
        {getSortedNextPlanUsages()}
      </Background>
    );
  };

  return (
    <>
      <Text as="div" style={{ marginBottom: removeNextPlanData ? 0 : 20 }}>
        Your current plan is limited to{" "}
        <span style={{ fontWeight: 700 }}>{currentUsageObjectText}.</span>
        {!removeNextPlanData && (
          <Text>Don't limit yourself and unlock Cornix's potential!</Text>
        )}
      </Text>
      {!removeNextPlanData && (
        <>
          {getHighlightedText()}
          <Button
            type="primary"
            style={{ width: "100%" }}
            onClick={() =>
              history.push(
                user?.isFree()
                  ? BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH
                  : UPGRADE_SUBSCRIPTION_SETTINGS_PATH
              )
            }
          >
            Upgrade now
          </Button>
        </>
      )}
    </>
  );
};

export default PassedQuotaContent;

const Background = styled.div`
  background-image: url(${(p) =>
    p.theme.isDarkMode ? darkModeBackgroundElement : backgroundElement});
  background-size: cover;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
  overflow: hidden;
  position: relative;
  padding: 20px;
  @media (max-width: 500px) {
    padding: 12px;
  }
`;
const AndMoreText = styled(Text)`
  font-weight: 500;
  margin-left: 20px;
  white-space: nowrap;
`;
